import type { SourceRoleSed, SourceRoleTruthFinding } from './sourceRoleTruthGuard';
import { effectivePhaseCapacity } from '../projectIntent';
import { buildNumericSourceEvidence } from '../grounding/numericSourceEvidence';

/** Compare only an adopted load-plus-project-date claim with a uniquely matched
 * frozen phase. A source publication or equipment procurement year is not COD. */
function conflicts(text: string, sed: SourceRoleSed): Array<{ sentence: string; phrase: string; load: string; expected: string }> {
  const phases = sed.fullProjectIntent?.phases ?? [];
  const targets = buildNumericSourceEvidence({ frozenProjectIntent: sed.fullProjectIntent }).phaseTargets ?? [];
  if (!phases.length || !targets.length) return [];
  const rows: Array<{ sentence: string; phrase: string; load: string; expected: string }> = [];
  for (const sentence of text.split(/(?<=[.!?])(?<!\bU\.S\.)\s+|\n+|;\s*|,?\s+(?:but|whereas)\s+/i)) {
    if (/\?\s*$/.test(sentence) || /\b(?:if|whether|hypothetical|scenario|historical|earlier|former|superseded|obsolete|not\s+(?:true|established)|incorrect)\b/i.test(sentence)
      || /\b(?:peer|comparable|other|another)\s+(?:project|site|campus)\b/i.test(sentence)
      || /^(?:According to|Per)\b|^(?:The\s+)?(?:owner|developer)\s+(?:documents?|proposal|meeting notes?)\s+(?:state|say|report|describe|target)s?\b/i.test(sentence.trim())) continue;
    if (/\b(?:timeline|target|schedule)\s+(?:is|remains)\s+(?:unknown|unconfirmed|unverified)\b/i.test(sentence)) continue;
    const match = /\b((?:the|this|our)\s+project['’]s\s+([\d,]+(?:\.\d+)?)\s*MW\s+demand)\s+and\s+((?:mid[- ]|late[- ]|early[- ]|Q[1-4]\s+)?(20\d{2}))\s+(?:timeline|completion\s+target)\b/i.exec(sentence);
    if (!match) continue;
    const value = Number(match[2]!.replace(/,/g, ''));
    const matching = phases.filter(phase => { const capacity = effectivePhaseCapacity(phase); return capacity.isExact && capacity.mw === value; });
    if (matching.length !== 1) continue;
    const target = targets.filter(row => row.phaseSequence === matching[0]!.sequence);
    if (target.length !== 1 || target[0]!.year === Number(match[4])) continue;
    const expected = `The frozen owner plan ties ${value} MW to Phase ${target[0]!.phaseSequence} with a ${target[0]!.quarter ? `Q${target[0]!.quarter} ` : ''}${target[0]!.year} planning target, not a delivery commitment. Keep phase targets distinct from project-wide completion, document dates and equipment availability; verify any changed schedule with the owner.`;
    rows.push({ sentence, phrase: match[0], load: match[1]!, expected });
  }
  return rows;
}

export function projectScheduleSourceScopeFindings(text: string, sed: SourceRoleSed, sectionId: string): SourceRoleTruthFinding[] {
  return conflicts(text, sed).map(row => ({ kind: 'owner-phase-schedule-mismatch', severity: 'block', sectionId,
    excerpt: row.sentence, observed: row.phrase, expected: row.expected, message: row.expected,
    sourcePaths: ['fullProjectIntent.phases'] }));
}

/** Qualify the adopted timing relation without inventing a new project COD or
 * changing the valid demand, attributed utility premise, or source tokens. */
export function repairProjectScheduleSourceScope(text: string, sed: SourceRoleSed): string | null {
  const rows = conflicts(text, sed);
  if (!rows.length) return null;
  let result = text;
  for (const row of rows) result = result.replace(row.sentence, row.sentence.replace(row.phrase,
    `${row.load}, with delivery subject to the separately recorded owner phase planning targets and schedule verification`));
  return result;
}
